import React, {Component} from 'react';
import styled from "styled-components";

import Holodeck from './Holodeck';
import Automation from './Automation';

import ControlSlider from '../midi/util/ControlSlider';

const CONTROL_BAR_WIDTH_PCT = 32;
const DISPLAY_REGION_WIDTH_PCT = 61;
const PAGE_MARGIN_PCT = 10;

const DEFAULT_INCLINATION = 45;
const DEFAULT_VIEW_ANGLE = -45;
const DEFAULT_FIELD_DEPTH = 60;
const DEFAULT_FIELD_SPAN = 75;

const AUTOMATION_VARIABLES = ["inclination", "view_angle", "field_depth", "field_span"];

const PageWrapper = styled.div`
    position: absolute;
    font-family: Arial;
    font-size: 1rem;
    overflow: hidden;
    height: 100%;
    margin: 0;
    top: 0;
    left: 0;
    right: 0;
    background-color: #fafafa;
    overflow-y: auto;
`;

const TitleBar = styled.div`
    position: fixed;
    top: 0 rem;
    left: 0;
    right: 0;
    height: 3.5rem;
    border-bottom: 0.15rem solid #bbbbbb;
    background: linear-gradient(to bottom right, #ffffff, #e7e7e7);
    z-index: 100;
    box-shadow: 0.25rem 0.25rem 3.5rem #e7e7e7
`;

const TitleText = styled.div`
    position: fixed;
    top: 1.45rem;
    left: 2.5rem;
    font-size: 2rem;
    color: #aaaaaa;
    letter-spacing: 0.5rem;
`;

const TitleBlurb = styled.div`
    position: fixed;
    font-family: Courier;
    font-weight: bold;
    top: 0.75rem;
    right: 2.5rem;
    font-size: 1.15rem;
    color: #ffffff;
    letter-spacing: 0.25rem;
    margin-right: 1rem;
    margin-top: 1.5rem;
    text-shadow: 0.25rem 0.25rem 0.5rem #aaaaaa;
`;

const PageBody = styled.div`
    position: relative;
    top: 4.5rem;
    bottom: 1rem;
    margin: 0 ${PAGE_MARGIN_PCT}%;
    background-color: white;
    border-radius: 0.5rem;
    padding: 1rem;
`;

const ControlBar = styled.div`
    display: inline-block;
    width: ${CONTROL_BAR_WIDTH_PCT}%;
    margin: 0.5%;
`;

const OrientationControls = styled.div`
    background-color: #f8f8f8;
    border-radius: 0.25rem;
    border: 0.15rem solid #aaaaaa;
    margin: 0.5% 0.5% 1.5%;
    padding: 0.5rem 0;
`;

const DisplayRegion = styled.div`
    display: inline-block;
    vertical-align: top;
    margin: 0.5%;
`;

const Checkbox = styled.input`
    padding: 1rem;
    margin: 0 0.5rem 0 1rem ;    
    background-color: #dddddd;
    vertical-align: text-bottom;
`;

const MainPrompt = styled.div`
    display: inline-block;
    font-size: 0.9rem;
    font-weight: bold;    
    vertical-align: middle;
`;

export class Render3d extends Component {

    state = {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        inclination: DEFAULT_INCLINATION,
        view_angle: DEFAULT_VIEW_ANGLE,
        field_depth: DEFAULT_FIELD_DEPTH,
        field_span: DEFAULT_FIELD_SPAN,
        ctx: null,
        canvas_ref: React.createRef(),
        automation_enabled: false
    };

    handleResize = (e) => {
        this.setState({
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight
        });
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
        const canvas = this.state.canvas_ref.current;
        if (!canvas) {
            console.log('no canvas');
            return;
        }
        const ctx = canvas.getContext('2d');
        this.setState({ctx: ctx});
    }

    to_radians(angle) {
        return angle * (Math.PI / 180);
    }

    get_value = (name) => {
        return this.state[name];
    }

    set_value = (name, value) => {
        this.setState({name: value});
    }

    set_all_values = (current_values) => {
        this.setState(current_values);
    }

    toggle_enabled = () => {
        this.setState({automation_enabled: !this.state.automation_enabled});
    }

    render() {
        const title_bar = <TitleBar>
            <TitleText>render 3d</TitleText>
            <TitleBlurb>so cool so cool soooo cooooool</TitleBlurb>
        </TitleBar>
        const scalar = (100 - 2 * PAGE_MARGIN_PCT) * this.state.innerWidth / 10000;
        const dimension = Math.min(DISPLAY_REGION_WIDTH_PCT * scalar, this.state.innerHeight * 0.80);
        const control_width = CONTROL_BAR_WIDTH_PCT * scalar;
        return <PageWrapper>
            {title_bar}
            <PageBody>
                <ControlBar>
                    <OrientationControls>
                        <ControlSlider
                            label={"inclination"}
                            min={-90} max={90} value={this.state.inclination}
                            width_container_px={control_width}
                            onChange={value => this.setState({inclination: value})}
                        />
                        <ControlSlider
                            label={"view angle"}
                            min={-180} max={180} value={this.state.view_angle}
                            width_container_px={control_width}
                            onChange={value => this.setState({view_angle: value})}
                        />
                        <ControlSlider
                            label={"field depth"}
                            min={1} max={200} value={this.state.field_depth}
                            width_container_px={control_width}
                            onChange={value => this.setState({field_depth: value})}
                        />
                        <ControlSlider
                            label={"field span"}
                            min={1} max={180} value={this.state.field_span}
                            width_container_px={control_width}
                            onChange={value => this.setState({field_span: value})}
                        />
                    </OrientationControls>
                    <Checkbox
                        type={"checkbox"}
                        checked={this.state.automation_enabled}
                        onChange={() => this.toggle_enabled()}/>
                    <MainPrompt>use automation</MainPrompt>
                    {this.state.automation_enabled && <Automation
                        get_value={name => this.get_value(name)}
                        set_value={(name, value) => this.set_value(name, value)}
                        set_all_values={current_values => this.set_all_values(current_values)}
                        variables={AUTOMATION_VARIABLES}
                        current_values={this.state}
                        canvas_ref={this.state.canvas_ref}
                        enabled={this.state.automation_enabled}
                    />}
                </ControlBar>
                <DisplayRegion>
                    <Holodeck
                        dimension={Math.floor(dimension / 2) * 2}
                        inclination={this.to_radians(this.state.inclination)}
                        view_angle={this.to_radians(this.state.view_angle)}
                        field_depth={this.state.field_depth}
                        field_span={this.to_radians(this.state.field_span)}
                        ctx={this.state.ctx}
                        canvas_ref={this.state.canvas_ref}
                    />
                </DisplayRegion>
            </PageBody>
        </PageWrapper>
    }
}

export default Render3d;
